// src/components/Pages/RefreshRadarDashboard.js

import React, { useState, useEffect, useMemo, useCallback, useDeferredValue } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Group,
    Select,
    Table,
    Loader,
    Center,
    Alert,
    RangeSlider,
    Stack,
    Pagination,
    Button,
    Modal,
    FileInput,
    Progress,
    Checkbox,
    Tooltip,
    TextInput,
    SegmentedControl,
    CloseButton
} from '@mantine/core';
import { IconAlertCircle, IconWorld, IconLanguage, IconRadar2, IconSearch, IconCloudUpload, IconClock, IconCalendar, IconChecks, IconArchive } from '@tabler/icons-react';
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

import { useBackgroundImage } from '../../context/BackgroundImageContext';
import { useAuth } from '../../context/AuthContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CMS_EDIT_URL_BASE = 'https://controlcentre.loveincorporated.com/msnglobal/Content/Gallery/Edit/';

const DistributionChart = ({ chartData, theme }) => {
    return (
        <Box mt="xl">
            <Text size="sm" fw={500} mb="xs">Candidate Score vs. Age Distribution</Text>
            <Box style={{ height: 200, width: '100%' }}>
                <ResponsiveContainer>
                    <ScatterChart margin={{ top: 10, right: 60, bottom: 20, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="age" type="number" name="Days Resting" label={{ value: "Days Resting", position: 'insideBottom', offset: -5, style: { fontSize: 12 } }} domain={['dataMin', 'dataMax']} tick={{ fontSize: 10 }} />
                        <YAxis dataKey="score" type="number" name="Score" label={{ value: "Score", angle: -90, position: 'insideBottom', offset: 70, style: { fontSize: 12 } }} domain={[0, 100]} tick={{ fontSize: 10 }} />
                        <Scatter name="Candidates" data={chartData} fill={theme.colors.blue[6]} shape={<circle r={1} fill={theme.colors.blue[6]} />} />
                    </ScatterChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

const MemoizedDistributionChart = React.memo(DistributionChart);

const StatusIndicator = ({ status, theme }) => {
    if (!status || status.status === 'None') {
        return null;
    }

    const statusConfig = {
        Unscheduled: {
            icon: <IconClock size={18} />,
            label: 'In pipeline, unscheduled',
            color: theme.colors.orange[6],
        },
        Scheduled: {
            icon: <IconCalendar size={18} />,
            label: `In pipeline, scheduled`,
            color: theme.colors.blue[5],
        },
        Completed: {
            icon: <IconChecks size={18} />,
            label: 'Completed in the last 7 days',
            color: theme.colors.green[6],
        },
        Archived: {
            icon: <IconArchive size={18} />,
            label: 'Archived in the last 7 days',
            color: theme.colors.gray[6],
        },
    };

    const config = statusConfig[status.status];
    if (!config) return null;

    return (
        <Tooltip label={config.label} withArrow color={config.color}>
            <span style={{ color: config.color }}>{config.icon}</span>
        </Tooltip>
    );
};

function RefreshRadarDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;
    const { currentUser, userProfile } = useAuth();

    // State
    const [candidates, setCandidates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [region, setRegion] = useState('US');
    const [language, setLanguage] = useState('EN');
    const [scoreRange, setScoreRange] = useState([0, 100]);
    const [scoreBounds, setScoreBounds] = useState([0, 100]);
    const [brand, setBrand] = useState('All');
    const [daysRange, setDaysRange] = useState([0, 365]);
    const [daysBounds, setDaysBounds] = useState([0, 365]);
    const [activePage, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(100); // Default remains 100
    const [liveScoreRange, setLiveScoreRange] = useState(scoreRange);
    const [liveDaysRange, setLiveDaysRange] = useState(daysRange);
    const [hasSearched, setHasSearched] = useState(false);
    const [opened, { open, close }] = useDisclosure(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadRegion, setUploadRegion] = useState('');
    const [uploadLanguage, setUploadLanguage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isScheduling, setIsScheduling] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    // NEW: Search Query State
    const [searchQuery, setSearchQuery] = useState('');

    // --- Memoized values and handlers ---
    const hasPrivilegedRole = useMemo(() =>
        userProfile && ['admin', 'management', 'editor'].includes(userProfile.role),
        [userProfile]
    );

    const [filtersChanged, setFiltersChanged] = useState(false);

    const fetchCandidates = useCallback(async (fetchConfig) => {
        const fetchRegion = fetchConfig?.region || region;
        const fetchLanguage = fetchConfig?.language || language;

        if (!fetchRegion || !fetchLanguage || !currentUser) return;

        setHasSearched(true);
        setIsLoading(true);
        setError(null);
        setCandidates([]);
        setSelectedIds([]);

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch('https://us-central1-freelancer-ideas-portal.cloudfunctions.net/getRefreshCandidates', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    region: fetchRegion,
                    language: fetchLanguage,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch data');
            }
            const data = await response.json();
            const candidatesData = data.candidates || [];
            setCandidates(candidatesData);
            setLastUpdated(data.lastUpdated ? new Date(data.lastUpdated).toLocaleString('en-GB') : 'N/A');

            if (candidatesData.length > 0) {
                const scores = candidatesData.map(c => c.refreshScore);
                const minScore = Math.floor(Math.min(...scores));
                const maxScore = Math.ceil(Math.max(...scores));
                setScoreBounds([minScore, maxScore]);
                setScoreRange([minScore, maxScore]);
                const days = candidatesData.map(c => c.lastUpdateDays);
                const minDays = Math.floor(Math.min(...days));
                const maxDays = Math.ceil(Math.max(...days));
                setDaysBounds([minDays > 0 ? minDays : 0, maxDays]);
                setDaysRange([minDays > 0 ? minDays : 0, maxDays]);
            }
            setFiltersChanged(false);
        } catch (err) {
            setError(err.message);
            setFiltersChanged(true);
        } finally {
            setIsLoading(false);
        }
    }, [region, language, currentUser]);

    const handleAddToSchedule = async () => {
        if (selectedIds.length === 0 || !currentUser) return;
        setIsScheduling(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch('https://us-central1-freelancer-ideas-portal.cloudfunctions.net/addToSchedule', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    items: candidates.filter(c => selectedIds.includes(c.originalId)),
                    region: region,
                    language: language,
                }),
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || 'Failed to add items to the schedule.');
            }

            const { addedCount, skippedCount } = result;
            if (addedCount > 0 && skippedCount > 0) {
                notifications.show({
                    title: 'Schedule Updated',
                    message: `Successfully added ${addedCount} new articles. ${skippedCount} items were already scheduled and were skipped.`,
                    color: 'green',
                });
            } else if (addedCount > 0) {
                notifications.show({
                    title: 'Success!',
                    message: `Successfully added ${addedCount} articles to the schedule.`,
                    color: 'green',
                });
            } else if (skippedCount > 0) {
                notifications.show({
                    title: 'No New Items Added',
                    message: `All ${skippedCount} selected articles are already in the schedule.`,
                    color: 'blue',
                });
            }

            setSelectedIds([]);

        } catch (err) {
            notifications.show({
                title: 'Error',
                message: err.message,
                color: 'red',
            });
        } finally {
            setIsScheduling(false);
        }
    };

    // --- UPDATED FILTER LOGIC (Includes Search) ---
    const filteredCandidates = useMemo(() => {
        return candidates.filter(c =>
            c.refreshScore >= scoreRange[0] && c.refreshScore <= scoreRange[1] &&
            c.lastUpdateDays >= daysRange[0] && c.lastUpdateDays <= daysRange[1] &&
            (brand === 'All' || c.Brand === brand) &&
            // Search Logic (Original ID OR Last Feed ID)
            (searchQuery === '' ||
                (c.originalId && String(c.originalId).toLowerCase().includes(searchQuery.toLowerCase())) ||
                (c['Last Feed ID'] && String(c['Last Feed ID']).toLowerCase().includes(searchQuery.toLowerCase()))
            )
        );
    }, [candidates, scoreRange, daysRange, brand, searchQuery]);

    const deferredItems = useDeferredValue(filteredCandidates);

    const chartData = useMemo(() => {
        return deferredItems.map(c => ({
            age: c.lastUpdateDays,
            score: c.refreshScore,
            title: c.latestTitle,
        }));
    }, [deferredItems]);

    const handleUpload = async () => {
        if (!uploadFile || !uploadRegion || !uploadLanguage) return;
        setIsUploading(true);
        setUploadProgress(0);
        const storage = getStorage();
        const storageRef = ref(storage, `refresh_uploads/${Date.now()}-${uploadFile.name}`);
        const metadata = { customMetadata: { 'region': uploadRegion, 'language': uploadLanguage } };
        const uploadTask = uploadBytesResumable(storageRef, uploadFile, metadata);
        uploadTask.on('state_changed', (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
        }, (error) => {
            setIsUploading(false);
            notifications.show({ title: 'Upload Failed', message: `An error occurred: ${error.code}`, color: 'red' });
        }, () => {
            setIsUploading(false);
            notifications.show({ title: 'Upload Successful!', message: 'The file will be processed in the background.', color: 'green' });
            close();
            setUploadFile(null);
            setUploadRegion('');
            setUploadLanguage('');
            setUploadProgress(0);
        });
    };

    // --- useEffect hooks ---
    useEffect(() => { setPage(1); }, [filteredCandidates]);
    useEffect(() => { setLiveScoreRange(scoreRange); }, [scoreRange]);
    useEffect(() => { setLiveDaysRange(daysRange); }, [daysRange]);
    useEffect(() => {
        const visibleIds = new Set(filteredCandidates.map(c => c.originalId));
        setSelectedIds(currentIds => currentIds.filter(id => visibleIds.has(id)));
    }, [filteredCandidates]);

    const handleRowSelect = useCallback((id) => {
        setSelectedIds(currentIds =>
            currentIds.includes(id)
                ? currentIds.filter(selectedId => selectedId !== id)
                : [...currentIds, id]
        );
    }, []);

    const handleSelectAll = useCallback((paginatedCandidates) => {
        const paginatedIds = paginatedCandidates.map(c => c.originalId);
        const allOnPageSelected = paginatedIds.every(id => selectedIds.includes(id));
        if (allOnPageSelected) {
            setSelectedIds(currentIds => currentIds.filter(id => !paginatedIds.includes(id)));
        } else {
            const newIds = paginatedIds.filter(id => !selectedIds.includes(id));
            setSelectedIds(currentIds => [...currentIds, ...newIds]);
        }
    }, [selectedIds]);

    // NEW: Pagination Handler
    const handleRowsPerPageChange = (value) => {
        setRowsPerPage(parseInt(value, 10));
        setPage(1); // Reset to first page to avoid empty views
    };

    useEffect(() => {
        const regionParam = searchParams.get('region');
        const languageParam = searchParams.get('language');
        const brandParam = searchParams.get('brand');

        if (regionParam) setRegion(regionParam);
        if (languageParam) setLanguage(languageParam);
        if (brandParam) setBrand(brandParam);

        if (regionParam || languageParam || brandParam) {
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams, setRegion, setLanguage, setBrand]);

    useEffect(() => {
        const regionParam = searchParams.get('region');
        const languageParam = searchParams.get('language');
        if (regionParam && languageParam) {
            fetchCandidates({ region: regionParam, language: languageParam });
        }
    }, [fetchCandidates, searchParams]);

    const formatDate = (dateString) => {
        if (!dateString || typeof dateString !== 'string') return 'N/A';
        const date = new Date(dateString + 'T00:00:00Z');
        if (isNaN(date.getTime())) return 'N/A';
        const day = date.getUTCDate();
        const year = date.getUTCFullYear().toString().slice(-2);
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[date.getUTCMonth()];
        let daySuffix;
        if (day > 3 && day < 21) {
            daySuffix = 'th';
        }
        else {
            switch (day % 10) {
                case 1: daySuffix = "st"; break;
                case 2: daySuffix = "nd"; break;
                case 3: daySuffix = "rd"; break;
                default: daySuffix = "th";
            }
        }
        return `${day}${daySuffix} ${month} '${year}`;
    };

    const RadarRow = React.memo(({ item, isSelected, onSelect, theme, isDark }) => {
        const handleSelect = useCallback(() => onSelect(item.originalId), [item.originalId, onSelect]);

        return (
            <Table.Tr style={{ backgroundColor: isSelected ? (isDark ? theme.colors.dark[5] : theme.colors.gray[1]) : undefined }}>
                <Table.Td style={{ textAlign: 'center', fontWeight: 500 }}>{item.refreshScore.toFixed(1)}%</Table.Td>
                <Table.Td>
                    <Checkbox checked={isSelected} onChange={handleSelect} />
                </Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>
                    <StatusIndicator status={item.status} theme={theme} />
                </Table.Td>
                <Table.Td>{item.latestTitle}</Table.Td>
                <Table.Td>{item.Brand || 'N/A'}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{item.pageViews.toLocaleString()}</Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>{item.lastUpdateDays}</Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>{formatDate(item.lastEntryDate)}</Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>
                    {item.originalId}
                </Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>{item.cdsEntries}</Table.Td>
                <Table.Td style={{ textAlign: 'center' }}>
                    {item['Last Feed ID'] &&
                        <Group justify="center" gap="xs" wrap="nowrap">
                            <Tooltip label={`Edit Last Variant: ${item['Last Feed ID']}`} withArrow>
                                <Button
                                    component="a"
                                    href={`${CMS_EDIT_URL_BASE}${item['Last Feed ID']}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="compact-xs"
                                    variant="outline"
                                    style={{ minWidth: 70, justifyContent: 'center' }}
                                >
                                    {item['Last Feed ID']}
                                </Button>
                            </Tooltip>
                        </Group>
                    }
                </Table.Td>
            </Table.Tr>
        );
    });

    const renderContent = () => {
        if (!hasSearched) {
            return <Center mt="xl" p="xl"><Text c="dimmed">Please set your filters and click "Get Candidates" to begin.</Text></Center>;
        }
        if (isLoading) {
            return <Center mt="xl"><Loader /><Text ml="md">Fetching candidates...</Text></Center>;
        }
        if (error) {
            return <Alert icon={<IconAlertCircle size="1rem" />} title="Error!" color="red" mt="xl">{error}</Alert>;
        }

        const totalResults = filteredCandidates.length;
        const totalPages = Math.ceil(totalResults / rowsPerPage);
        const start = (activePage - 1) * rowsPerPage;
        const end = start + rowsPerPage;
        const paginatedCandidates = deferredItems.slice(start, end);
        const paginatedIds = paginatedCandidates.map(c => c.originalId);
        const allOnPageSelected = paginatedIds.length > 0 && paginatedIds.every(id => selectedIds.includes(id));
        const someOnPageSelected = paginatedIds.some(id => selectedIds.includes(id));

        return (
            <Stack>
                <Paper withBorder shadow="md" p="md" mt="xl" radius="md">

                    <Group justify="space-between" align="center" mb="md" style={{ minHeight: '36px' }}>
                        <Box>
                            {selectedIds.length > 0 ? (
                                <Button
                                    color="blue"
                                    onClick={handleAddToSchedule}
                                    loading={isScheduling}
                                    disabled={isScheduling}
                                >
                                    Add {selectedIds.length} to Schedule
                                </Button>
                            ) : (
                                <Text size="md">Please select items to schedule...</Text>
                            )}
                        </Box>

                        {/* UPDATED: Pagination Control Group */}
                        <Box>
                            {totalResults > 0 && (
                                <Group>
                                    <Text size="sm" c="dimmed">Rows:</Text>
                                    <SegmentedControl
                                        value={String(rowsPerPage)}
                                        onChange={handleRowsPerPageChange}
                                        data={[
                                            { label: '25', value: '25' },
                                            { label: '50', value: '50' },
                                            { label: '100', value: '100' },
                                        ]}
                                        size="xs"
                                    />
                                    <Pagination total={totalPages} value={activePage} onChange={setPage} withEdges />
                                </Group>
                            )}
                        </Box>
                    </Group>

                    <Table.ScrollContainer minWidth={1400}>
                        <Table striped highlightOnHover verticalSpacing="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Tooltip label="Combines performance and age rules to prioritse suitable articles." withArrow>
                                        <Table.Th style={{ textAlign: 'center' }}>Candidate Score</Table.Th>
                                    </Tooltip>

                                    <Table.Th>
                                        <Checkbox
                                            checked={allOnPageSelected}
                                            indeterminate={someOnPageSelected && !allOnPageSelected}
                                            onChange={() => handleSelectAll(paginatedCandidates)}
                                        />
                                    </Table.Th>
                                    <Tooltip label="Indicates if an article is in the pipeline, or was recently completed/archived." withArrow>
                                        <Table.Th style={{ textAlign: 'center' }}>Status</Table.Th>
                                    </Tooltip>
                                    <Table.Th>Article (Latest Title)</Table.Th>
                                    <Table.Th>Brand</Table.Th>
                                    <Table.Th style={{ textAlign: 'right' }}>Page Views</Table.Th>
                                    <Table.Th style={{ textAlign: 'center' }}>Days Resting</Table.Th>
                                    <Table.Th style={{ textAlign: 'center' }}>Last Push Date</Table.Th>
                                    <Table.Th style={{ textAlign: 'center' }}>Original ID</Table.Th>
                                    <Table.Th style={{ textAlign: 'center' }}>Variants</Table.Th>
                                    <Table.Th style={{ textAlign: 'center' }}>Edit Latest</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {paginatedCandidates.map(c => <RadarRow key={c.originalId} item={c} isSelected={selectedIds.includes(c.originalId)} onSelect={handleRowSelect} theme={theme} isDark={isDark} />)}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    {filteredCandidates.length === 0 && !isLoading && (
                        <Text ta="center" py="xl" c="dimmed">No candidates found for the selected criteria.</Text>
                    )}

                    {totalResults > 0 && (
                        <Group justify="flex-end" mt="xl">
                            <Pagination total={totalPages} value={activePage} onChange={setPage} withEdges />
                        </Group>
                    )}
                </Paper>
            </Stack>
        );
    };

    return (
        <Box
            style={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none', backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', overflowY: 'scroll' }}
        >
            <Container size="xl" pt="xl">
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconRadar2 color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }} />
                    Resell Radar
                </Title>
                <Text size="xl" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Identify and prioritise articles for updates based on performance and age.
                </Text>
                <Paper withBorder shadow="sm" p="md" radius="md">
                    <Group grow>
                        <Select label="Region" leftSection={<IconWorld size={16} />} data={['UK', 'US', '--', 'AE', 'AR', 'AT', 'AU', 'BE', 'CA', 'CH', 'CL', 'CO', 'DE', 'ES', 'FR', 'IE', 'IN', 'MX', 'MY', 'NZ', 'PE', 'PH', 'SG', 'XL', 'ZA']} value={region} onChange={(value) => { setRegion(value || ''); setCandidates([]); setLastUpdated(null); setFiltersChanged(true); }} />
                        <Select label="Language" leftSection={<IconLanguage size={16} />} data={['EN', 'DE', 'ES', 'FR']} value={language} onChange={(value) => { setLanguage(value || ''); setCandidates([]); setLastUpdated(null); setFiltersChanged(true); }} />
                        <Select label="Brand" leftSection={<IconRadar2 size={16} />} data={['All', 'loveMONEY', 'loveFOOD', 'loveEXPLORING', 'lovePROPERTY']} value={brand} onChange={(value) => { setBrand(value || 'All'); setFiltersChanged(true); }} />

                        {/* UPDATED: Search Input with Clear Button */}
                        <TextInput
                            label="Search IDs"
                            placeholder="Original ID / Feed ID"
                            leftSection={<IconSearch size={16} />}
                            rightSection={
                                searchQuery.length > 0 ? (
                                    <CloseButton
                                        aria-label="Clear search"
                                        onClick={() => setSearchQuery('')}
                                        style={{ cursor: 'pointer' }}
                                    />
                                ) : null
                            }
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.currentTarget.value)}
                        />
                    </Group>

                    <Box mt="md" pt="md">
                        <Text size="sm" fw={500}>Filter by Candidate Score ({liveScoreRange[0]}% - {liveScoreRange[1]}%)</Text>
                        <RangeSlider value={liveScoreRange} onChange={setLiveScoreRange} onChangeEnd={(value) => { setScoreRange(value); setFiltersChanged(true); }} min={scoreBounds[0]} max={scoreBounds[1]} step={1} label={(value) => `${value}%`} mt="lg" />
                    </Box>
                    <Box mt="md" pt="md">
                        <Text size="sm" fw={500}>Filter by Days Since Last Publication ({liveDaysRange[0]} - {liveDaysRange[1]} days)</Text>
                        <RangeSlider value={liveDaysRange} onChange={setLiveDaysRange} onChangeEnd={(value) => { setDaysRange(value); setFiltersChanged(true); }} min={daysBounds[0]} max={daysBounds[1]} step={1} label={(value) => value} mt="lg" />
                    </Box>

                    {hasSearched && !isLoading && chartData.length > 0 && (
                        <MemoizedDistributionChart chartData={chartData} theme={theme} />
                    )}

                    <Group justify="space-between" align="flex-end" mt="xl">
                        <Stack gap={4}>
                            {hasSearched && !isLoading && (
                                <Group gap="xs" align="baseline">
                                    <Text size="xl" fw={700}>{filteredCandidates.length.toLocaleString()}</Text>
                                    <Text c="dimmed" size="sm">Matching Candidates</Text>
                                </Group>
                            )}
                            <Text size="xs" c="dimmed">
                                {hasSearched && `Data for ${region} (${language}) last uploaded: ${lastUpdated || 'Loading...'}`}
                            </Text>
                        </Stack>
                        <Group>
                            {hasPrivilegedRole && (
                                <Button variant="outline" color="green" onClick={open} leftSection={<IconCloudUpload size={18} />}>
                                    Upload Data
                                </Button>
                            )}
                            <Button variant="outline" color="blue" onClick={() => navigate(`/resell-preparation?region=${region}&language=${language}&brand=${brand}`)}>
                                Go to Resell Prep
                            </Button>
                            <Button onClick={() => fetchCandidates()} color={filtersChanged ? "green" : "green"} loading={isLoading} leftSection={<IconSearch size={18} />}>
                                Get Candidates
                            </Button>
                        </Group>
                    </Group>
                </Paper>
            </Container>

            <Modal opened={opened} onClose={close} title="Upload Resell Radar Data" centered>
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        Using the Power BI 'Canonical History'.'Originals, Grouped' table, export as 'Data with current layout'.
                        Your .xlsx file will download locally. Set the region/language below before uploading.
                    </Text>
                    <Group grow>
                        <Select label="Region" placeholder="Select region" leftSection={<IconWorld size={16} />} data={['UK', 'US', '--', 'AE', 'AR', 'AT', 'AU', 'BE', 'CA', 'CH', 'CL', 'CO', 'DE', 'ES', 'FR', 'IE', 'IN', 'MX', 'MY', 'NZ', 'PE', 'PH', 'SG', 'XL', 'ZA']} value={uploadRegion} onChange={(value) => setUploadRegion(value || '')} disabled={isUploading} required />
                        <Select label="Language" placeholder="Select language" leftSection={<IconLanguage size={16} />} data={['EN', 'DE', 'ES', 'FR']} value={uploadLanguage} onChange={(value) => setUploadLanguage(value || '')} disabled={isUploading} required />
                    </Group>
                    <FileInput label="Refresh Data File (.xlsx)" placeholder="Click to select file" value={uploadFile} onChange={setUploadFile} leftSection={<IconCloudUpload size={16} />} accept=".xlsx" disabled={isUploading} required clearable />
                    {isUploading && (
                        <Progress value={uploadProgress} animated size="lg" radius="sm" />
                    )}
                    <Button fullWidth mt="md" onClick={handleUpload} loading={isUploading} disabled={!uploadFile || !uploadRegion || !uploadLanguage || isUploading} leftSection={<IconCloudUpload size={18} />}>
                        Upload and Process File
                    </Button>
                </Stack>
            </Modal>

            <Container fluid px="xl" pb="xl">
                {renderContent()}
            </Container>
        </Box>
    );
}

export default RefreshRadarDashboard;